/**
 * sanitizeMalformedLinks.ts - Fix malformed markdown/HTML link combinations
 * 
 * Transforms patterns like:
 *   [Link Text](<a href="url">url</a>)
 * Into proper HTML:
 *   <a href="url" target="_blank" rel="noopener noreferrer">Link Text</a>
 * 
 * Also ensures all <a> tags have target="_blank" and rel="noopener noreferrer"
 */

export function sanitizeMalformedLinks(html: string | undefined | null): string {
    if (!html) return '';
  
    let result = html;
  
    // Pattern 1: [Text](<a href="url">url</a>) - malformed markdown/HTML mix
    result = result.replace(
      /\[([^\]]+)\]\(<a\s+href=["']([^"']+)["'][^>]*>[^<]*<\/a>\)/gi,
      (_, text, href) => `<a href="${href}" target="_blank" rel="noopener noreferrer" style="color: #11AB5E;">${text}</a>`
    );
  
    // Pattern 2: [Text](url) - standard markdown links (not already in HTML)
    result = result.replace(
      /\[([^\]]+)\]\((?!<)(https?:\/\/[^\s)]+)\)/gi,
      (_, text, href) => `<a href="${href}" target="_blank" rel="noopener noreferrer" style="color: #11AB5E;">${text}</a>`
    );
  
    // Pattern 3: Add target="_blank" and rel="noopener noreferrer" to all existing <a> tags that don't have them
    result = result.replace(
      /<a\s+([^>]*?)>/gi,
      (_match, attributes) => {
        // Check if target="_blank" already exists
        const hasTarget = /target\s*=\s*["'][^"']*["']/i.test(attributes);
        const hasRel = /rel\s*=\s*["'][^"']*["']/i.test(attributes);
        
        let newAttributes = attributes;
        
        // Add target="_blank" if not present
        if (!hasTarget) {
          newAttributes += ' target="_blank"';
        }
        
        // Add or update rel attribute to include noopener noreferrer
        if (!hasRel) {
          newAttributes += ' rel="noopener noreferrer"';
        } else {
          // If rel exists, ensure it includes noopener noreferrer
          newAttributes = newAttributes.replace(
            /rel\s*=\s*["']([^"']*)["']/i,
            (_relMatch, relValue) => {
              const relValues = relValue.split(/\s+/).filter(Boolean);
              if (!relValues.includes('noopener')) relValues.push('noopener');
              if (!relValues.includes('noreferrer')) relValues.push('noreferrer');
              return `rel="${relValues.join(' ')}"`;
            }
          );
        }
        
        return `<a ${newAttributes}>`;
      }
    );
  
    return result;
  }
  
  export default sanitizeMalformedLinks;